import React, { useState } from 'react';
import { DndContext, DragOverlay, useSensor, useSensors, PointerSensor, TouchSensor } from '@dnd-kit/core';
import { Sparkles } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useDaggerheartData } from '../../../context/DataContext';
import ModifierToken from './trait-selection/ModifierToken';
import TraitSlot from './trait-selection/TraitSlot';
import useTraitDragDrop from './trait-selection/useTraitDragDrop';

/**
 * TraitsSection - Token pool drag-and-drop trait assignment
 *
 * Must assign: +2, +1, +1, 0, 0, -1 to the six traits
 *
 * Supports:
 * - Drag tokens from pool to trait slots
 * - Auto-swap: Dragging to filled slot returns old value to pool
 */
function TraitsSection({ character, onUpdate }) {
  const allData = useDaggerheartData();
  const traitNames = ['Agility', 'Strength', 'Finesse', 'Instinct', 'Presence', 'Knowledge'];

  // Get class-specific guidance if available
  const classGuidance = character.class && allData?.classGuidance?.[character.class];
  const suggestedTraits = classGuidance?.suggestedTraits;

  // Initialize traits if not present
  const traits = character.traits || {
    Agility: null,
    Strength: null,
    Finesse: null,
    Instinct: null,
    Presence: null,
    Knowledge: null
  };

  // Handle traits update
  function handleTraitsUpdate(updatedTraits) {
    onUpdate({
      traits: updatedTraits
    });
  }

  // Apply suggested traits from class guidance
  function applySuggestedTraits() {
    if (!suggestedTraits) return;

    onUpdate({
      traits: { ...suggestedTraits }
    });
  }

  // Track what's being dragged for smooth DragOverlay
  const [activeId, setActiveId] = useState(null);
  const [draggedValue, setDraggedValue] = useState(null);

  // Use the drag-and-drop hook
  const {
    availableModifiers,
    handleDragEnd
  } = useTraitDragDrop(traits, handleTraitsUpdate);

  // Calculate how many modifiers are left
  const remainingCount = availableModifiers.length;

  // Handle drag start - capture what's being dragged
  function handleDragStart(event) {
    setActiveId(event.active.id);
    setDraggedValue(event.active.data.current.value);
  }

  // Wrap handleDragEnd to clear drag state
  function handleDragEndWrapper(event) {
    handleDragEnd(event);
    setActiveId(null);
    setDraggedValue(null);
  }

  // Require 5px movement before drag starts (supports both mouse and touch)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Must move 5px before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150, // Hold for 150ms before drag starts (prevents scroll interference)
        tolerance: 5, // Allow 5px of movement during delay
      },
    })
  );

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEndWrapper}>
      <div className="space-y-6">
        {/* Instructions */}
        <div>
          <p className="text-gray-300 text-sm">
            Drag tokens from the pool to assign them to trait slots
          </p>
        </div>

        {/* Quick Apply Button */}
        {suggestedTraits && (
          <button
            onClick={applySuggestedTraits}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white rounded-lg font-medium transition-all shadow-lg"
          >
            <Sparkles className="w-5 h-5" />
            Apply Suggested Traits for {character.class}
          </button>
        )}

        {/* Token Pool - Only show tokens not yet assigned */}
        {availableModifiers.length > 0 && (
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="mb-3">
              <p className="text-sm text-gray-300">
                Available Tokens
                <span className="ml-2 font-bold text-white">
                  ({remainingCount} remaining)
                </span>
              </p>
            </div>

            <div className="flex flex-wrap gap-3 justify-center">
              {availableModifiers.map((value, index) => (
                <ModifierToken
                  key={`pool-${value}-${index}`}
                  id={`pool-${value}-${index}`}
                  value={value}
                />
              ))}
            </div>
          </div>
        )}

        {/* Trait Slots Grid - Compact boxes in rows */}
        <div>
          <div className="grid grid-cols-3 gap-4 lg:gap-3">
            {traitNames.map(trait => (
              <TraitSlot
                key={trait}
                traitName={trait}
                value={traits[trait]}
              />
            ))}
          </div>
        </div>

        {/* All assigned celebration */}
        {remainingCount === 0 && (
          <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 text-center">
            <p className="text-green-400 font-bold">✓ All tokens assigned!</p>
          </div>
        )}
      </div>

      {/* DragOverlay for smooth, non-jittery dragging */}
      <DragOverlay dropAnimation={null}>
        {draggedValue !== null ? (
          <ModifierToken
            id="overlay"
            value={draggedValue}
            disabled={false}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default TraitsSection;
